import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';

type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user, updateUser } = useAuth();
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem('korvex-theme');
    return stored === 'light' ? 'light' : 'dark';
  });

  useEffect(() => {
    if (user) {
      setThemeState(user.darkMode ? 'dark' : 'light');
    }
  }, [user?.id, user?.darkMode]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.classList.toggle('light', theme === 'light');
    localStorage.setItem('korvex-theme', theme);
  }, [theme]);

  const setTheme = (next: Theme) => {
    setThemeState(next);
    if (user && (next === 'dark') !== user.darkMode) {
      void import('../api/client').then(({ api }) =>
        api.updateSettings({ darkMode: next === 'dark' }).then(({ user: updated }) => updateUser(updated)),
      );
    }
  };

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  const value = useMemo(() => ({ theme, toggleTheme, setTheme }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
